// =============================================================
// 檔案名稱：regions.js
// 版本：V1.02
// 日期：2025/07/19
// 主要功能與職責：
//   區域權限系統的常數定義，包含北中南東四區的縣市對應表。
// 這個檔案是做什麼的？
//   定義台灣北中南東四區的縣市劃分，提供區域代碼、名稱和縣市對應關係，
//   作為整個區域權限系統的基礎常數。
// 解決了什麼問題？
//   統一管理區域定義，避免硬編碼，提供可維護的區域對應關係。
// 更新歷史：
// V1.02 - 修正區域劃分，根據正確的地理劃分調整縣市歸屬
//   - 宜蘭縣：從東區移至北區
//   - 苗栗縣：從北區移至中區  
//   - 澎湖縣：從東區移至南區
//   - 金門縣、連江縣：保留在東區（外島）
// V1.01 - 初版建立
// 注意事項：
//   此檔案為區域權限系統的核心常數，修改時需同步更新相關功能。
// 關連檔案：regionUtils.js, AdminPanel.js, BookingModal.js
// =============================================================

// 區域代碼常數
export const REGION_CODES = {
    NORTH: 'north',      // 北區
    CENTRAL: 'central',  // 中區
    SOUTH: 'south',      // 南區
    EAST: 'east'         // 東區
};

// 區域名稱常數
export const REGION_NAMES = {
    [REGION_CODES.NORTH]: '北區',
    [REGION_CODES.CENTRAL]: '中區',
    [REGION_CODES.SOUTH]: '南區',
    [REGION_CODES.EAST]: '東區'
};

// 區域圖示常數
export const REGION_ICONS = {
    [REGION_CODES.NORTH]: '🏙️',
    [REGION_CODES.CENTRAL]: '🏢',
    [REGION_CODES.SOUTH]: '🌴',
    [REGION_CODES.EAST]: '🏔️'
};

// 區域顏色常數
export const REGION_COLORS = {
    [REGION_CODES.NORTH]: 'blue',
    [REGION_CODES.CENTRAL]: 'green',
    [REGION_CODES.SOUTH]: 'orange',
    [REGION_CODES.EAST]: 'purple'
};

// 縣市到區域的對應表
export const CITY_TO_REGION = {
    // 北區
    '基隆市': REGION_CODES.NORTH,
    '臺北市': REGION_CODES.NORTH,
    '新北市': REGION_CODES.NORTH,
    '桃園市': REGION_CODES.NORTH,
    '新竹市': REGION_CODES.NORTH,
    '新竹縣': REGION_CODES.NORTH,
    '宜蘭縣': REGION_CODES.NORTH,
    
    // 中區
    '苗栗縣': REGION_CODES.CENTRAL,
    '臺中市': REGION_CODES.CENTRAL,
    '彰化縣': REGION_CODES.CENTRAL,
    '南投縣': REGION_CODES.CENTRAL,
    '雲林縣': REGION_CODES.CENTRAL,
    
    // 南區
    '嘉義市': REGION_CODES.SOUTH,
    '嘉義縣': REGION_CODES.SOUTH,
    '臺南市': REGION_CODES.SOUTH,
    '高雄市': REGION_CODES.SOUTH,
    '屏東縣': REGION_CODES.SOUTH,
    '澎湖縣': REGION_CODES.SOUTH,
    
    // 東區
    '花蓮縣': REGION_CODES.EAST,
    '臺東縣': REGION_CODES.EAST,
    '金門縣': REGION_CODES.EAST,
    '連江縣': REGION_CODES.EAST
};

// 區域到縣市的對應表
export const REGION_TO_CITIES = {
    [REGION_CODES.NORTH]: [
        '基隆市', '臺北市', '新北市', '桃園市', 
        '新竹市', '新竹縣', '宜蘭縣'
    ],
    [REGION_CODES.CENTRAL]: [
        '苗栗縣', '臺中市', '彰化縣', '南投縣', '雲林縣'
    ],
    [REGION_CODES.SOUTH]: [
        '嘉義市', '嘉義縣', '臺南市', '高雄市', '屏東縣', '澎湖縣'
    ],
    [REGION_CODES.EAST]: [
        '花蓮縣', '臺東縣', '金門縣', '連江縣'
    ]
};

// 所有區域代碼陣列
export const ALL_REGIONS = Object.values(REGION_CODES);

// 所有縣市陣列
export const ALL_CITIES = Object.keys(CITY_TO_REGION);

// 預設區域權限（用於資料遷移）
export const DEFAULT_REGIONS = [REGION_CODES.NORTH, REGION_CODES.CENTRAL, REGION_CODES.SOUTH, REGION_CODES.EAST]; 